<template>
  <div class="tender-container-wrapper">
    <div class="tender-container clearfix">
      <h4>
        <HKLLeftRight>
          <template #left>
            <span>{{ $t('tender.name') }}</span>
            <i
              id="tender_refresh"
              class="fa fa-refresh dark flat icon_refresh"
              @click="loadTenderTable()"
            />
          </template>
          <template #right>
            <HKLButton
              class="btn-reset"
              action="resetFilter"
              color="darkblue"
              @click="() => resetFilterObject()"
            />
          </template>
        </HKLLeftRight>
      </h4>
      <div class="tender-table">
        <template>
          <table class="hover">
            <thead>
              <tr>
                <th
                  v-for="header in itemHeaders"
                  @click="sortBy(header.code)"
                  :class="header.class"
                  :key="header.name"
                >
                  {{ header.name }} {{ orderbyAttr === header.code ? reverse ? '&#9660;' : '&#9650;' : null }}
                </th>
              </tr>
              <tr>
                <th
                  v-for="header in itemHeaders"
                  :key="header.code"
                >
                  <component
                    :is="header.filterComponent"
                    v-model="filterObject[header.code]"
                    v-bind="header.filterComponentAttrs || {}"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in orderedItems"
                :key="item.id"
              >
                <td>
                  <a
                    :class="{ link_text: canGoToDetail(item), 'no-finger': !canGoToDetail(item) }"
                    @click="navToPage(item)"
                  >{{ item.prNumber }}</a>
                </td>
                <td>
                  <a
                    :class="{ link_text: canGoToDetail(item), 'no-finger': !canGoToDetail(item) }"
                    @click="navToPage(item)"
                  >{{ item.prDescription }}</a>
                </td>
                <td>{{ item.site }}</td>
                <td class="number_text"><span v-if="item.showQIndicator">(Q) </span>{{ item.isRepresentative ? item.statusText : item.status }}</td>
                <td class="number_text">
                  <span v-if="item.updateDate">{{ dateUtil.formatDate(item.updateDate, true, true) }}</span>
                </td>
                <td :class="{ 'number_text': true, 'closing-date-red': item.isRepresentative}">
                  <span>{{ item.closingDate && dateUtil.formatDate(item.closingDate, true, true) || '--' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { ref, unref, computed, onMounted } from 'vue-demi'
import dateUtil from '@/utils/dateUtil'
import router from '@/router'
import { formatReviewTender } from './utils'
import { getBids, parseVendorBidStatus } from '@/api/bids/request'
import { getReviewList } from '@/api/tenders/request'
import { getRevisionByVersionNum } from '@/api/tender-addendums/request'
import { navToDetailPageExt } from '@/utils/navigation'
import { queryStatusMap, tenderStatus, vendorBidStatus } from '@/utils/locale'
import useRegionRef from '@/utils/useRegionRef'
import DatePicker from '@/controls/DatePicker.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import { filterValueFn } from '../utils'

const cachedFilterObject = useRegionRef({}, { logoutReset: true })

export default {
  name: 'tenders-bid-table',
  template: 'tenders-bid-table',
  components: {
    HKLLeftRight,
    HKLButton,
  },
  props: {
    loadingTable: String,
    model: Array,
    vendorId: String,
    isVendorAccount: {
      type: Boolean,
      default: false,
    },
    isTenderRep: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const itemHeaders = computed(() => [
      {
        code: 'prNumber',
        name: 'Number',
        class: 'number-header',
        filterNoCase: true,
      },
      {
        code: 'prDescription',
        name: 'Name',
        class: 'name-header',
        filterNoCase: true,
      },
      {
        code: 'site',
        name: 'Site',
        class: 'site-header',
        filterNoCase: true,
      },
      {
        code: 'status',
        name: 'Status',
        class: 'number_text status-header',
        filterNoCase: true,
        onFilterFn: (_, filterValue, __, item) => {
          if (/^\s*\(Q\)/.test(value) && !item.showQIndicator) return false
          const value = item.isRepresentative ? item.statusText : item.status
          return value.replace(/^\s*\(Q\)/, '').includes(filterValue)
        },
      },
      {
        code: 'updateDate',
        name: 'Modified On',
        class: 'number_text date-header',
        filterComponent: DatePicker,
        filterComponentAttrs: {
          calendarButton: true,
          calendarButtonIcon: 'fa fa-calendar',
          canPutTotop: false,
        },
        onFilterFn: (value, filterValue) => {
          const targetDate = dateUtil.getStartOfDay(value)
          const filterDate = dateUtil.getStartOfDay(filterValue)
          return targetDate === filterDate
        },
      },
      {
        code: 'closingDate',
        name: 'Closing Date',
        class: 'number_text date-header',
        filterComponent: DatePicker,
        filterComponentAttrs: {
          calendarButton: true,
          calendarButtonIcon: 'fa fa-calendar',
          canPutTotop: false,
        },
        onFilterFn: (value, filterValue) => {
          const targetDate = dateUtil.getStartOfDay(value)
          const filterDate = dateUtil.getStartOfDay(filterValue)
          return targetDate === filterDate
        },
      },
    ].map((item) => Object.assign({ filterComponent: HKLInput }, item)))
    const initFilterObject = () => {
      return unref(itemHeaders).reduce((obj, { code, filterComponentDefaultValue }) => {
        obj[code] = filterComponentDefaultValue || ''
        return obj
      }, {})
    }
    onMounted(() => {
      if (Object.keys(cachedFilterObject.value).length > 0) return
      cachedFilterObject.value = initFilterObject()
    })

    const orderbyAttr = ref('')
    const reverse = ref(1)
    const items = ref([])
    const filteredItems = computed(() => {
      return unref(items).filter((item, index) => {
        return unref(itemHeaders).every(({ code, onFilterFn, filterNoCase = false }) => {
          const filterValue = unref(cachedFilterObject)[code]
          const value = item[code]
          const result = filterValueFn(value, filterValue, {
            noCase: filterNoCase,
            customDiffFn: typeof onFilterFn === 'function' ? (a, b) => onFilterFn(a, b, index, item) : undefined,
          })
          return result
        })
      })
    })
    return {
      orderbyAttr,
      reverse,
      items,
      filteredItems,
      itemHeaders,
      filterObject: cachedFilterObject,
      resetFilterObject () {
        cachedFilterObject.value = initFilterObject()
        orderbyAttr.value = ''
        reverse.value = 1
      },
    }
  },
  data () {
    return {
      loadingTenderTable: false,
      tenderRawList: [],
      navigableStatus: [vendorBidStatus.INVITED, vendorBidStatus.DRAFT, vendorBidStatus.DECLINED, vendorBidStatus.SUBMITTED, 'A-INVITED', queryStatusMap.ISSUED],
      addendumStatus: [tenderStatus.DRAFT, tenderStatus.WAPPR, tenderStatus.APPR, tenderStatus.REJECTED],
      groupOrderBy: 'updateDate',
      dateUtil,
    }
  },
  methods: {
    navToPage (item) {
      if (item.isRepresentative) {
        navToDetailPageExt(item)
      } else if (this.navigableStatus.indexOf(item.status) >= 0) {
        router.push({
          name: 'tenderBidView',
          params: {
            id: item.id,
          },
        })
      }
    },
    canGoToDetail (item) {
      return item.isRepresentative || this.navigableStatus.indexOf(item.status) >= 0
    },
    getModifiedOn (record) {
      const queryUpdatedAt = this.getQueryLastModifiedDate(deep(record, 'bid.queries'))
      const vendorUpdatedAt = record.bid && record.bid.vendorUpdatedAt
      if (queryUpdatedAt) {
        return +new Date(queryUpdatedAt) > +new Date(vendorUpdatedAt) ? queryUpdatedAt : vendorUpdatedAt
      }
      return vendorUpdatedAt
    },
    async getTenderRawList () {
      if (this.preload) {
        await new Promise(resolve => setTimeout(resolve, 100))
        return this.preloadedTenderBids
      } else {
        const list = await getBids()
        return list
      }
    },
    async loadTenderTable () {
      this.loadingTenderTable = true
      this.items = []

      if (this.isVendorAccount) {
        const bids = await getBids()
        const formatedBids = (await Promise.all(bids.map(this.formatBids))).filter(item => item.id)
        this.items.push(...lodash.orderBy(formatedBids, this.groupOrderBy))
      }

      if (this.isTenderRep) {
        const reviewList = await getReviewList()
        const formatedTenders = reviewList.map(formatReviewTender)
        this.items.push(...lodash.orderBy(formatedTenders, this.groupOrderBy))
      }

      this.loadingTenderTable = false
    },

    async formatBids (record) {
      if (!record.tender) {
        return {}
      }
      const shownAttrs = {}
      shownAttrs.id = record.bid.id
      shownAttrs.prNumber = record.tender.prNumber ? record.tender.prNumber : '--'
      shownAttrs.prDescription = record.tender.prDescription ? record.tender.prDescription : '--'

      shownAttrs.status = parseVendorBidStatus(record.tender, record.bid)
      if (shownAttrs.status) {
        shownAttrs.site = record.tender && record.tender.site ? record.tender.site : '--'
        shownAttrs.version = record.tender.version ? record.tender.version : 0
        shownAttrs.updateDate = this.getModifiedOn(record)
        if (shownAttrs.status.startsWith('Q')) {
          shownAttrs.closingDate = this.getQueryClosingDate(deep(record, 'bid.queries'))
        } else {
          shownAttrs.closingDate = await this.getTenderClosingDate(record.tender)
        }
        shownAttrs.tenderType = record.tender.tenderType
        if (([queryStatusMap.ISSUED, queryStatusMap.SUBMITTED].includes(shownAttrs.status) ||
          (shownAttrs.closingDate && +new Date(shownAttrs.closingDate) >= +new Date()))) {
          return shownAttrs
        }
      }
      return {}
    },
    async getTenderClosingDate (tender = {}) {
      let closingDate = tender.closingDate
      if (tender.version && this.addendumStatus.includes(tender.status)) {
        const revision = await getRevisionByVersionNum(tender.id, tender.version - 1)
        closingDate = deep(revision, 'closingDate')
      }
      return closingDate
    },

    getQueryClosingDate (queries = []) {
      const lastQuery = queries.reduce((last, query) => {
        return last && new Date(last.createdAt).getTime() > new Date(query.createdAt).getTime() ? last : query
      }, {})
      return lastQuery.closingDate
    },
    getQueryLastModifiedDate (queries = []) {
      const sortedQueries = lodash.orderBy(queries, query => {
        return new Date(query.createdAt).getTime()
      }, ['desc'])
      if (sortedQueries.length === 1) {
        return sortedQueries[0].vendorUpdatedAt
      } else if (sortedQueries.length > 1) {
        return sortedQueries[0].vendorUpdatedAt || sortedQueries[1].submittedDate
      }
    },

    sortBy (attr) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
    },
  },
  async mounted () {
    await this.loadTenderTable()
  },
  watch: {
    items () {
      this.$emit('update:model', this.items)
    },
  },
  computed: {
    orderedItems () {
      if (this.orderbyAttr) {
        const result = lodash.orderBy(this.filteredItems, this.orderbyAttr)
        return this.reverse ? result.reverse() : result
      }
      return this.filteredItems
    },
  },
}
</script>

<style scoped src="@/assets/styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
.no-finger {
  cursor: default;
}

.number-header {
  width: 10%
}
.name-header {
  width: 40%;
}
.status-header {
  width: 10%
}
.site-header {
  width: 10%
}
.date-header {
  width: 15%
}

h4 {
  &:has(.btn-reset) {
    padding-bottom: 8px;
  }
  & > .hkl-left-right {
    align-items: center;
    span + .icon_refresh {
      margin-left: 5px;
    }
  }
}
</style>
